import { forEach, map } from 'lodash-es';
import { ConfigUrl, UrlParts } from './index';

const DEFAULT_URL_PREFIX = 'https://localhost';

export function isUrlString(configUrl: ConfigUrl | undefined): configUrl is string {
  return typeof configUrl === 'string';
}

export function coerceToUrlParts(configUrl: ConfigUrl): UrlParts {
  if (isUrlString(configUrl)) {
    return stringToUrlParts(configUrl);
  }
  return configUrl;
}

function coerceToConfigUrl(orig: ConfigUrl, urlParts: UrlParts) {
  if (isUrlString(orig)) {
    return urlPartsToString(urlParts);
  }
  return urlParts;
}

function leftTrim(s: string, char: string) {
  if (s.startsWith(char)) {
    s = s.substr(char.length);
  }
  return s;
}

function paramsFromQuery(query: string | undefined): Record<string, string> {
  if (!query) {
    return {};
  }
  if (query.startsWith('?')) {
    query = query.substr(1);
  }
  const params: Record<string, string> = {};
  query.split('&').forEach((param) => {
    const [name, value] = param.split('=', 2);
    params[name] = value;
  });
  return params;
}

function addUrlPart(url: string, separator: string, part: string | undefined) {
  if (!part) {
    return url;
  }
  part = leftTrim(part, separator);
  return `${url}${separator}${part}`;
}

function queryFromParams(params: Record<string, string>) {
  return map(params, (value, name) => `${name}=${value}`).join('&');
}

function urlPartRequired(part: string | undefined) {
  if (!part) {
    throw new Error(
      'Cannot buildUrl(). Must have at least a scheme and hostname, and no location was available.',
    );
  }
  return part;
}

export function stringToUrlParts(urlStr: string) {
  let relativeUrl = false;
  if (urlStr[0] === '/') {
    relativeUrl = true;
    // the URL constructor wants a full url, so if this looks like a relative url,
    // make it look like a full url
    urlStr = DEFAULT_URL_PREFIX + urlStr;
  }
  const url = new URL(urlStr);
  const urlParts: UrlParts = {
    host: url.hostname,
    // trim off the ending :
    scheme: url.protocol.substr(0, url.protocol.length - 1),
  };
  if (url.hash) {
    // trim off the starting #
    urlParts.fragment = url.hash.substr(1);
  }
  if (url.pathname && url.pathname !== '/') {
    urlParts.path = url.pathname;
  }
  if (url.port) {
    urlParts.port = parseInt(url.port);
  }
  const query = url.searchParams.toString();
  if (query) {
    urlParts.query = query;
  }
  if (relativeUrl) {
    delete urlParts.host;
    delete urlParts.scheme;
  }
  return urlParts;
}

/**
 * Turn the passed `urlParts` into an actual url.
 *
 * @param urlParts
 */
export function urlPartsToString(urlParts: UrlParts) {
  let url = '';
  if (urlParts.scheme || urlParts.host || urlParts.port) {
    url += `${urlPartRequired(urlParts.scheme)}://${urlPartRequired(urlParts.host)}`;
    url = addUrlPart(url, ':', urlParts.port?.toString());
  }
  url = addUrlPart(url, '/', urlParts.path);
  url = addUrlPart(url, '?', urlParts.query);
  url = addUrlPart(url, '#', urlParts.fragment);
  return url;
}

export function withQueryParams(
  configUrl: ConfigUrl,
  queryParams: Record<string, string | undefined>,
) {
  const urlParts = coerceToUrlParts(configUrl);
  const params = paramsFromQuery(urlParts.query);

  forEach(queryParams, (value, name) => {
    if (value === undefined) {
      return;
    }
    params[name] = encodeURIComponent(value);
  });
  urlParts.query = queryFromParams(params);

  return coerceToConfigUrl(configUrl, urlParts);
}

/**
 * Returns the passed url, without its search part (everything after ?).
 *
 * @param configUrl
 */
export function withoutQuery(configUrl: ConfigUrl) {
  const urlParts = coerceToUrlParts(configUrl);
  delete urlParts.query;
  return coerceToConfigUrl(configUrl, urlParts);
}
